import { activityDayIso } from './activityStats';
import { inRange } from './dashboardRange';
import { leadName } from './types';
import type { ActivitySlim } from './activityStats';
import type { DateRange } from './dashboardRange';
import type { Lead, Membership } from './types';

/**
 * What actually moved — Kyu's fourth Monday-morning question.
 *
 * ── What used to be here, and why it went ──────────────────────────────────
 *
 * 8j built two blocks: "Needs you today" (Q2) and this one (Q4). Kyu removed
 * the first from the live page, 2026-08-27:
 *
 *   "Do not keep Needs You Today as a dashboard block. That belongs in the
 *   Tasks area / task workflow, not in the main dashboard. Dashboard should
 *   stay focused on overview, not become a task list."
 *
 * and, asked whether the half of it with nowhere to go should be rebuilt in
 * Tasks: "Do not keep half of it there, and do not force the first-touch half
 * into Tasks if it doesn't naturally belong there. If the alert banner already
 * points to that queue, that is enough for the dashboard."
 *
 * So `todaysWork` is deleted rather than moved. The task half was already
 * covered by the Tasks tab's Overdue and Due today filters; the first-touch
 * half is reachable from the alert banner at the top of this page and from
 * the Awaiting 1st Touch figure on each cohort card.
 *
 * ── The one question left, and the two things that changed in it ───────────
 *
 * It FOLLOWS THE RANGE now instead of a fixed seven days. Kyu chose the fixed
 * window in 8j when it was the only time concept on the screen; with a range
 * control above it, two different windows on one page is a page that has to be
 * explained.
 *
 * And it counts REPLIES RECORDED ON MEMBERSHIPS, which it did not before —
 * see below. That was not a refactor. It was a hole.
 */

/** A capped list needs a cap. "This Year" can be hundreds of events. */
export const MOVED_LIST_CAP = 10;

export type MovedKind = 'reply' | 'meeting' | 'converted';

export const MOVED_LABELS: Record<MovedKind, string> = {
  reply: 'Replied',
  meeting: 'Meeting booked',
  converted: 'Converted to deal',
};

export interface MovedEvent {
  leadId: string;
  name: string;
  company: string;
  kind: MovedKind;
  /** ISO day. */
  on: string;
}

/** One deal, as much of it as this needs. */
export interface DealSlim {
  lead_id: string | null;
  created_at: string;
}

export interface WhatMoved {
  /** Newest first, capped at MOVED_LIST_CAP. */
  events: MovedEvent[];
  counts: Record<MovedKind, number>;
  /** Everything in the range, not just what fits. */
  total: number;
}

/**
 * Replies in, meetings booked, and leads converted, inside the range.
 *
 * ── The hole this closes ───────────────────────────────────────────────────
 *
 * 8j read replies from the timeline alone: `lead_activity.kind =
 * 'reply_received'`. That is not where every reply is.
 *
 * `markReplied` in the Outreach grid — live, in use, today — writes
 * `reply_date` and `seq_status: 'replied'` onto the membership row and writes
 * NOTHING to the timeline. So a reply marked the way the outreach cycle is
 * actually worked never appeared in "what moved", and the block quietly said
 * less had happened than had. activityStats has carried this dual-source note
 * since batch 4 and this block was written without honouring it.
 *
 * Both sources are read here, deduplicated by lead and day: one reply recorded
 * both ways is one reply, and listing it twice would be its own small lie.
 *
 * `deals` is passed in rather than derived. A conversion has no dated event
 * anywhere else the dashboard loads: the promote RPC writes a `note` whose
 * only marker is the words "Promoted to deal:" in its BODY, and the activity
 * query deliberately does not fetch bodies. `deals.created_at` is exact, at
 * the cost of one more query and of the permission caveat in the caller.
 */
export function whatMoved(
  activities: ActivitySlim[],
  memberships: Membership[],
  deals: DealSlim[],
  leads: Lead[],
  range: DateRange,
  cap: number = MOVED_LIST_CAP,
): WhatMoved {
  const byId = new Map(leads.map((lead) => [lead.id, lead]));
  const events: MovedEvent[] = [];
  const seen = new Set<string>();

  const push = (leadId: string, kind: MovedKind, iso: string) => {
    const lead = byId.get(leadId);
    // No lead, no row: every line here is meant to be clicked into.
    if (lead === undefined) {
      return;
    }
    const day = iso.slice(0, 10);
    const key = `${leadId}:${kind}:${day}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    events.push({ leadId, name: leadName(lead), company: lead.company_name, kind, on: day });
  };

  for (const activity of activities) {
    // inRange also rejects a future timestamp, because every range ends today
    // or earlier. Clock skew and hand-entered dates both produce them, and a
    // thing that has not happened yet is not a thing that moved.
    if (!inRange(activity.occurred_at, range)) {
      continue;
    }
    if (activity.kind === 'reply_received') {
      push(activity.lead_id, 'reply', activityDayIso(activity));
    } else if (activity.kind === 'meeting') {
      push(activity.lead_id, 'meeting', activity.occurred_at);
    }
  }

  // The other half of "replied". See the note above.
  for (const membership of memberships) {
    if (membership.reply_date !== null && inRange(membership.reply_date, range)) {
      push(membership.lead_id, 'reply', membership.reply_date);
    }
  }

  for (const deal of deals) {
    if (deal.lead_id === null || !inRange(deal.created_at, range)) {
      continue;
    }
    push(deal.lead_id, 'converted', deal.created_at);
  }

  // Newest first: "what moved" is read from the top and the most recent thing
  // is the most interesting.
  events.sort((a, b) => (a.on > b.on ? -1 : a.on < b.on ? 1 : 0));

  const counts: Record<MovedKind, number> = { reply: 0, meeting: 0, converted: 0 };
  for (const event of events) {
    counts[event.kind] += 1;
  }

  // The counts describe EVERYTHING in the range; the list is capped. They are
  // computed before the slice for exactly that reason — a count taken from the
  // capped list would top out at the cap and read as "that is all of it".
  return { events: events.slice(0, cap), counts, total: events.length };
}
